import type { ComboboxItem } from '@mantine/core';
import type { SerializedEditorState } from 'lexical';
import { create } from 'zustand';
import type {
	CanvasSectionConfigs,
	ParsedCanvasFrontend,
	TCanvasFrontend,
	TFlowItemFrontend,
	TGroupItemFrontend,
	TLibraryFrontend,
	TMentionOption
} from '@/types/common';
import { TCardSize } from '@/types/common';
import { AppEvent } from '@/types/event-emitter.types.ts';
import { emitter } from '@/utils/event-emitter.ts';
import { parseCanvas } from '@/utils/parse-canvas.ts';

type ComparisonCard = {
	title: SerializedEditorState;
	idn: string;
	body: SerializedEditorState;
};

type DragState = {
	columnId: string;
	fromIndex: number;
	toIndex: number;
} | null;

interface IFlowStoreState {
	cardSize: TCardSize;
	setCardSize: (cardSize: TCardSize) => void;
	dragState: DragState;
	setDragState: (dragState: DragState) => void;
	mentionOptions: TMentionOption[];
	typesOptions: ComboboxItem[];
	setMentionOptions: (mentionOptions: TMentionOption[]) => void;
	setTypesOptions: (typesOptions: ComboboxItem[]) => void;
	openFlowDrawer: (
		selectedFlowItem?: TFlowItemFrontend,
		groupType?: string
	) => void;
	closeFlowDrawer: () => void;
	setFlowDrawerViewMode: (mode: FlowDrawerHeaderMode) => void;
	toggleComparingMode: (defaultCardToCompare?: ComparisonCard) => void;
	setCardToCompare: (card: ComparisonCard | null) => void;
	flowDrawer: {
		isOpen: boolean;
		flowItem?: TFlowItemFrontend;
		groupType?: string;
		isFormattedView: boolean;
		isComparingMode: boolean;
		cardToCompare: ComparisonCard | null;
	};
	canvas: ParsedCanvasFrontend;
	canvasSectionConfig: CanvasSectionConfigs;
	setCanvas: (canvas: TCanvasFrontend) => void;
	itemsDictionary: Record<string, TGroupItemFrontend>;
	setItemsDictionary: (
		itemsDictionary: Record<string, TGroupItemFrontend>
	) => void;
	addItem: (typeIdn: string, item: TGroupItemFrontend) => void;
	updateItem: (typeIdn: string, item: TGroupItemFrontend) => void;
	deleteItem: (typeIdn: string, itemIdn: string) => void;
	reorderItems: (typeIdn: string, fromIdx: number, toIdx: number) => void;
	library: TLibraryFrontend;
	setLibrary: (library: TLibraryFrontend) => void;
	resetState: () => void;
}

const createInitialState = () => ({
	cardSize: TCardSize.COMPACT,
	dragState: null as DragState,
	mentionOptions: [],
	typesOptions: [],
	flowDrawer: {
		isOpen: false,
		flowItem: undefined,
		groupType: undefined,
		isFormattedView: false,
		isComparingMode: false,
		cardToCompare: null
	},
	library: [],
	canvas: {
		sections: {},
		configs: {}
	},
	canvasSectionConfig: {},
	itemsDictionary: {}
});

const useFlowStore = create<IFlowStoreState>()((set, get) => {
	return {
		...createInitialState(),
		resetState: () => set(createInitialState()),
		setCardToCompare: (card: ComparisonCard | null) => {
			return set((state) => ({
				...state,
				flowDrawer: {
					...state.flowDrawer,
					cardToCompare: card,
					isComparingMode: !!card
				}
			}));
		},
		toggleComparingMode: (cardToCompare) => {
			const { flowDrawer } = get();
			const nextComparing = !flowDrawer.isComparingMode;

			if (!nextComparing) {
				emitter.emit(AppEvent.UPDATE_LEXICAL_EDITOR_VIEW_MODE, {
					isFormattedView: flowDrawer.isFormattedView
				});
			}

			return set((state) => ({
				flowDrawer: {
					...state.flowDrawer,
					isComparingMode: nextComparing,
					isFormattedView: false,
					...(nextComparing
						? { cardToCompare: cardToCompare || null }
						: { cardToCompare: null })
				}
			}));
		},

		setFlowDrawerViewMode: (mode) => {
			const isFormattedView = mode === FlowDrawerHeaderMode.FORMATTED;

			set((state) => ({
				...state,
				flowDrawer: {
					...state.flowDrawer,
					isFormattedView
				}
			}));

			emitter.emit(AppEvent.UPDATE_LEXICAL_EDITOR_VIEW_MODE, {
				isFormattedView
			});
		},
		setCardSize: (cardSize) => set({ cardSize }),
		setDragState: (dragState) => set({ dragState }),
		setMentionOptions: (mentionOptions) => set({ mentionOptions }),
		setTypesOptions: (typesOptions) => set({ typesOptions }),
		openFlowDrawer: (selectedFlowItem, groupType) => {
			set({
				flowDrawer: {
					isOpen: true,
					flowItem: selectedFlowItem,
					groupType,
					isFormattedView: false,
					isComparingMode: false,
					cardToCompare: null
				}
			});
		},
		setItemsDictionary: (itemsDictionary) => set({ itemsDictionary }),
		closeFlowDrawer: () => {
			set({
				flowDrawer: {
					isOpen: false,
					flowItem: undefined,
					groupType: undefined,
					isFormattedView: false,
					isComparingMode: false,
					cardToCompare: null
				}
			});
		},
		setLibrary: (library) => set({ library }),
		setCanvas: (canvas) => {
			const { sections, configs } = parseCanvas(canvas);
			return set({ canvas: { sections, configs } });
		},
		addItem: (typeIdn, item) => {
			set((state) => {
				const currentSection = state.canvas.sections[typeIdn];
				return {
					canvas: {
						...state.canvas,
						sections: {
							...state.canvas.sections,
							[typeIdn]: currentSection.concat(item)
						}
					}
				};
			});
			emitter.emit(AppEvent.SCROLL_TO_CARD, { cardId: item.idn });
		},
		updateItem: (typeIdn, item) =>
			set((state) => {
				const currentSection = state.canvas.sections[typeIdn];
				return {
					canvas: {
						...state.canvas,
						sections: {
							...state.canvas.sections,
							[typeIdn]: currentSection.map((itm) =>
								itm.idn === item.idn ? item : itm
							)
						}
					}
				};
			}),

		deleteItem: (typeIdn, itemIdn) =>
			set((state) => {
				const currentSection = state.canvas.sections[typeIdn];
				return {
					canvas: {
						...state.canvas,
						sections: {
							...state.canvas.sections,
							[typeIdn]: currentSection.filter((itm) => itm.idn !== itemIdn)
						}
					}
				};
			}),

		reorderItems: (typeIdn, fromIdx, toIdx) =>
			set((state) => {
				const items = [...(state.canvas.sections[typeIdn] ?? [])];
				const [removed] = items.splice(fromIdx, 1);
				items.splice(toIdx, 0, removed);
				return {
					canvas: {
						...state.canvas,
						sections: { ...state.canvas.sections, [typeIdn]: items }
					}
				};
			})
	};
});

export enum FlowDrawerHeaderMode {
	MARKDOWN = 'markdown',
	FORMATTED = 'formatted'
}

export default useFlowStore;
export type { IFlowStoreState };
